import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { AuthTerminalLoading } from '@/components/auth/AuthTerminalLoading';
import { API_BASE_URL } from '@/lib/api';

export function GitHubCallback() {
 const [searchParams] = useSearchParams();
 const navigate = useNavigate();
 const { oauthLogin } = useAuth();
 const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
  'loading'
 );
 const [error, setError] = useState<string>('');
 const hasProcessed = useRef(false);

 useEffect(() => {
  const handleCallback = async () => {
   // Prevent duplicate processing in React StrictMode
   if (hasProcessed.current) {
    return;
   }
   hasProcessed.current = true;

   const code = searchParams.get('code');
   const state = searchParams.get('state');
   const errorParam = searchParams.get('error');

   if (errorParam) {
    setStatus('error');
    setError('GitHub authentication was cancelled or failed');
    setTimeout(() => navigate('/login'), 5000);
    return;
   }

   if (!code || !state) {
    setStatus('error');
    setError('Missing authorization code or state parameter');
    setTimeout(() => navigate('/login'), 5000);
    return;
   }

   try {
    const response = await fetch(
     `${API_BASE_URL}/api/auth/oauth/github/callback?code=${code}&state=${state}`,
     {
      method: 'GET',
      headers: {
       'Content-Type': 'application/json',
      },
     }
    );

    const data = await response.json();

    if (!response.ok) {
     throw new Error(data.error?.message || 'OAuth callback failed');
    }

    if (data.success) {
     // Store auth data
     oauthLogin(data.data.user, data.data.token, data.data.refreshToken);
     setStatus('success');

     // Check if user needs onboarding (new user or incomplete profile)
     const isNewUser =
      data.data.isNewUser || !data.data.user.onboardingCompleted;

     // Redirect after a brief success message
     setTimeout(() => {
      if (isNewUser) {
       navigate('/onboarding');
      } else {
       navigate('/dashboard');
      }
     }, 2000);
    } else {
     throw new Error(data.message || 'Authentication failed');
    }
   } catch (err) {
    console.error('OAuth callback error:', err);
    setStatus('error');
    setError(err instanceof Error ? err.message : 'Authentication failed');

    // Redirect to login after 5 seconds on error
    setTimeout(() => {
     navigate('/login');
    }, 5000);
   }
  };

  handleCallback();
 }, [searchParams, navigate, oauthLogin]);

 if (status === 'loading') {
  return (
   <AuthTerminalLoading
    title='GitHub Authentication'
    subtitle='Establishing Secure Proxy Link'
    steps={[
     'Connecting to GitHub OAuth Gateway...',
     'Exchanging authorization code...',
     'Validating session signature...',
     'Retrieving encrypted profile data...',
     'Initializing workspace access...',
    ]}
   />
  );
 }

 if (status === 'success') {
  return (
   <AuthTerminalLoading
    status='success'
    title='Access Granted'
    subtitle='Identity Verified'
    steps={[
     'GitHub Handshake Complete',
     'Encryption Keys Synchronized',
     'Session Token Generated',
     'Workspace Handover Initiated...',
    ]}
   />
  );
 }

 return (
  <AuthTerminalLoading
   status='error'
   title='Authentication Failure'
   subtitle='System Access Denied'
   error={error}
   steps={[
    'GitHub Handshake Failed',
    'Clearing volatile memory...',
    'Logging security event...',
    'Redirecting to login portal...',
   ]}
  />
 );
}
